import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import {
  Button,
  ButtonGroup,
  Container,
  Divider,
  Dropdown,
  Grid,
  Icon,
  Segment,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../app/layout/PageHeader";
import { useStore } from "../../app/stores/store";
import ConfirmDelete from "../../app/common/form/ConfirmDelete";
import { AccessLevel, TicketItemType } from "../../app/models/enums";
import LoadingComponent from "../../app/layout/LoadingComponent";
import PartItemForm from "../tickets/dashboard/modals/PartItemForm";
import { formatCurrency } from "../../app/common/util/functions";
import PartDetailsVendors from "./PartDetailsVendors";
import PartDetailsLocations from "./PartDetailsLocations";
import PartDetailsTickets from "./PartDetailsTickets";
import { PartInventoryFormValues } from "../../app/models/inventory";
import EditNotificationSettingsModal from "./EditNotificationSettingsModal";
import PartDetailsCoreTracking from "../coreTracking/PartDetailsCoreTracking";

export default observer(function PartDetails() {
  const { t } = useTranslation(["common", "customer", "inventory"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    modalStore,
    userStore,
    inventoryIndexStore,
    inventoryPartDetailsStore,
    inventorySettingsStore,
    vendorStore,
  } = useStore();

  const { checkAccessLevel } = userStore;
  const { vendorDropdownList, loadDropdownListVendors } = vendorStore;
  const { loadDropdownListStockLocations, updatePartInStock, removePart } =
    inventoryIndexStore;
  const {
    selectedPartDetails,
    selectedPartDetailsVendors,
    selectedPartDetailsLocations,
    loadInventoryPartDetails,
    loadInventoryPartVendors,
    loadInventoryPartLocations,
    loadInventoryPartCores,
    loadInventoryPartTickets,
    loadingInitial,
  } = inventoryPartDetailsStore;

  const {
    inventorySettings,
  } = inventorySettingsStore;

  const loadAllPartDetails = async (id: string) => {
    loadInventoryPartDetails(id)
      .then(async () => {
        await loadInventoryPartVendors(id);
        await loadInventoryPartLocations(id);
      })
      .then(async () => {
        await loadInventoryPartCores(id);
        await loadInventoryPartTickets(id);
      });
  };

  useEffect(() => {
    loadDropdownListVendors().then(() => {
      loadDropdownListStockLocations();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      loadAllPartDetails(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    loadInventoryPartDetails,
    loadInventoryPartVendors,
    loadInventoryPartLocations,
    loadInventoryPartTickets,
  ]);

  if (loadingInitial)
    return (
      <LoadingComponent
        content={t("partDetails_loading", { ns: "inventory" })}
      />
    );

  return (
    <>
      <PageHeader
        header={`${t("partDetails", { ns: "inventory" })}: ${selectedPartDetails?.partNumber ?? ""}`}
        type={"h1"}
        divider={true}
        getAlerts={true}
        addTitle={false}
      />
      <Container className="page">
        <Segment>
          <Grid columns={2}>
            <Grid.Column>
              <Grid>
                <Grid.Row columns={2} className="lessPaddingSysAdminTables">
                  <Grid.Column textAlign="right">
                    <span className="intro">
                      {t("part_number", { ns: "reports" }) + ":"}
                    </span>
                  </Grid.Column>
                  <Grid.Column>{selectedPartDetails?.partNumber}</Grid.Column>
                </Grid.Row>
                <Grid.Row columns={2} className="lessPaddingSysAdminTables">
                  <Grid.Column textAlign="right">
                    <span className="intro">
                      {t("description", { ns: "common" }) + ":"}
                    </span>
                  </Grid.Column>
                  <Grid.Column>{selectedPartDetails?.partName}</Grid.Column>
                </Grid.Row>
                <Grid.Row columns={2} className="lessPaddingSysAdminTables">
                  <Grid.Column textAlign="right">
                    <span className="intro">{t("labels.totalQuantity", { ns: "inventory" }) + ":"}</span>
                  </Grid.Column>
                  <Grid.Column>
                    {selectedPartDetails?.quantityInStock}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid.Column>
            <Grid.Column>
              <Grid>
                <Grid.Row columns={2} className="lessPaddingSysAdminTables">
                  <Grid.Column className="textAlignRight">
                    <span className="intro">{t("avg_cost", { ns: "reports" }) + ":"}</span>
                  </Grid.Column>
                  <Grid.Column>
                    {formatCurrency(selectedPartDetails?.averageCost ?? 0)}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={2} className="lessPaddingSysAdminTables">
                  <Grid.Column textAlign="right">
                    <span className="intro">{t("retail_price", { ns: "reports" }) + ":"}</span>
                  </Grid.Column>
                  <Grid.Column>
                    {formatCurrency(selectedPartDetails?.unitPrice ?? 0)}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={2} className="lessPaddingSysAdminTables">
                  <Grid.Column textAlign="right">
                    <span className="intro">{t("labels.coreTracking", { ns: "inventory" }) + ":"}</span>
                  </Grid.Column>
                  <Grid.Column>
                    {selectedPartDetails?.coreTrackingEnabled ? (
                      <Icon name="check" color="green" size='large' />
                    ) : null}
                  </Grid.Column>
                </Grid.Row>
                {/* Will add this part back when we add multiple shop locations */}
                {/* <Grid.Row columns={2}>
                  <Grid.Column textAlign="right">
                    <span className="intro">Location:</span>
                  </Grid.Column>
                  <Grid.Column>2012 Goliax St</Grid.Column>
                </Grid.Row> */}
              </Grid>
            </Grid.Column>
          </Grid>
        </Segment>
        <Grid>
          <Grid.Column>
            <Button
              className="save_button mobile_buttons"
              icon="edit"
              color="grey"
              content={t("Edit Part", { ns: "common" })}
              onClick={() => {
                modalStore.openModal(
                  <PartItemForm
                    part={{
                      partId: selectedPartDetails?.id ?? undefined,
                      partCode: selectedPartDetails?.partNumber ?? "",
                      description: selectedPartDetails?.partName ?? "",
                      location: selectedPartDetails?.location,
                      locationId: Array.from(
                        selectedPartDetailsLocations.values()
                      ).length
                        ? Array.from(selectedPartDetailsLocations.values())[0]
                            .id
                        : undefined,
                      vendorId:  selectedPartDetails?.vendorId,
                      unitPrice: selectedPartDetails?.averageCost ?? 0,
                      rate: selectedPartDetails?.unitPrice ?? 0,
                      quantity: selectedPartDetails?.quantityInStock !== undefined ? selectedPartDetails?.quantityInStock : 0,
                      reOrderLevel: selectedPartDetails?.reOrderLevel ?? 0,
                      partMarkup: Array.from(
                        selectedPartDetailsVendors?.values()
                      ).length
                        ? Array.from(selectedPartDetailsVendors?.values())[0]
                            .markup
                        : 0,
                      notes: Array.from(selectedPartDetailsLocations.values())
                        .length
                        ? Array.from(selectedPartDetailsLocations.values())[0]
                            .notes
                        : undefined,
                      coreTrackingEnabled:
                        selectedPartDetails?.coreTrackingEnabled ?? false,
                      lowInventoryReminderEnabled:
                        selectedPartDetails?.lowInventoryReminderEnabled ??
                        false,
                      coreValue: selectedPartDetails?.coreValue ?? 0,
                      returned: selectedPartDetails?.returned ?? false,
                      refunded: selectedPartDetails?.refunded ?? false,
                      isTaxable: true,
                    }}
                    isAdd={false}
                    addItem={async (
                      values: PartInventoryFormValues,
                      type: TicketItemType
                    ) => {
                      await updatePartInStock({
                        partId: values.partId ?? selectedPartDetails?.id,
                        partNumber: values.partCode ?? "",
                        description: values.description ?? "",
                        location: values.location,
                        locationId: values.locationId,
                        vendorId: values.vendorId,
                        cost: values.unitPrice ?? 0,
                        price: values.rate ?? 0,
                        quantity: values.quantity,
                        reOrderLevel: values.reOrderLevel ?? 0,
                        markup: values.partMarkup ?? 0,
                        coreTrackingEnabled:
                          values.coreTrackingEnabled ?? false,
                        lowInventoryReminderEnabled:
                          values.lowInventoryReminderEnabled ?? false,
                        coreValue: values.coreValue ?? 0,
                        returned: values.returned ?? false,
                        refunded: values.refunded ?? false,
                        notes: values.notes
                      });
                      await loadAllPartDetails(
                        values.id ?? selectedPartDetails?.id!
                      );
                    }}
                    autoCapitalizeFirstLetter={false}
                    vendorDropdownList={vendorDropdownList}
                    forInventory={true}
                  />
                );
              }}
            />

            <ButtonGroup color="orange" className="save_button mobile_buttons">
              <Button>{t("reports", { ns: "reports" })}</Button>
              <Dropdown
                className="button icon dropdown_button"
                floating
                trigger={<></>}
              >
                <Dropdown.Menu>
                  <Dropdown.Item
                    id="reportsVendor"
                    key={"vendor"}
                    as={Link}
                    onClick={() => {
                      navigate(
                        `/reports/vendor?partId=${selectedPartDetails!.id}`,
                        {
                          state: { location },
                        }
                      );
                    }}
                    text={t("labels.vendorHistory", { ns: "inventory" })}
                    icon="dolly"
                  />
                  <Dropdown.Item
                    id="reportsPart"
                    key={"partSales"}
                    as={Link}
                    onClick={() => {
                      navigate(
                        `/reports/partSales?partId=${selectedPartDetails!.id}`,
                        { state: { location } }
                      );
                    }}
                    text={t("labels.partHistory", { ns: "inventory" })}
                    icon="car"
                  />
                  <Dropdown.Item
                    id="reportsPurchaseHistory"
                    key={"partSales"}
                    as={Link}
                    onClick={() => {
                      navigate(`/inventoryIndex/purchaseHistory/${id}`);
                    }}
                    text={t("purchaseHistory", { ns: "inventory" })}
                    icon="dollar"
                  />
                </Dropdown.Menu>
              </Dropdown>
            </ButtonGroup>
            {inventorySettings?.enableNotifyLowInventory && <Button
              color="grey"
              className="save_button mobile_buttons"
              icon="clock"
              type="button"
              content={t("Edit Notification Setting", { ns: "customer" })}
              onClick={() => {
                modalStore.openModal(<EditNotificationSettingsModal />);
              }}
            />}
            {checkAccessLevel(AccessLevel.Manager) && (
              <Button
                color="red"
                className="save_button mobile_buttons"
                icon="trash"
                type="button"
                content={t("Delete Part", { ns: "customer" })}
                onClick={() => {
                  modalStore.openModal(
                    <ConfirmDelete
                      header={t("removePart", {
                        ns: "inventory",
                      })}
                      value={id}
                      text={""}
                      deleteValue={(d) => {
                        removePart(d).then(() => {
                          navigate("/inventoryIndex");
                        });
                      }}
                    />
                  );
                }}
              />
            )}
            <Button
              className="save_button mobile_buttons"
              as={Link}
              to="/inventoryIndex"
              icon="cancel"
              type="button"
              content={t("cancel", { ns: "common" })}
            />
          </Grid.Column>
        </Grid>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column>
              <PageHeader
                header={t("vendors.vendor", { ns: "miscSettings" })}
                type={"h3"}
                divider={false}
                addTitle={false}
              />
            </Grid.Column>
          </Grid.Row>
          <Divider></Divider>
          <Grid.Row>
            <Grid.Column>
              <PartDetailsVendors id={id ?? ""} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column></Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column verticalAlign="bottom">
              <PageHeader
                header={t("labels.locations", { ns: "inventory" })}
                type={"h3"}
                divider={false}
                addTitle={false}
              />
            </Grid.Column>
          </Grid.Row>
          <Divider></Divider>
          <Grid.Row>
            <Grid.Column>
              <PartDetailsLocations id={id ?? ""} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column></Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <PageHeader
                header={t("labels.coreTracking", { ns: "inventory" })}
                type={"h3"}
                divider={false}
                addTitle={false}
              />
            </Grid.Column>
          </Grid.Row>
          <Divider></Divider>
          <Grid.Row>
            <Grid.Column>
              <PartDetailsCoreTracking id={id ?? ""} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column></Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <PageHeader
                header={t("tickets", { ns: "customer" })}
                type={"h3"}
                divider={false}
                addTitle={false}
              />
            </Grid.Column>
          </Grid.Row>
          <Divider></Divider>
          <Grid.Row>
            <Grid.Column>
              <PartDetailsTickets id={id ?? ""} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    </>
  );
});
